import { useState } from "react";
import { Outlet, NavLink, Navigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, Tag, Truck, PackageCheck, MapPin, Building2,
  Star, Newspaper, Image as ImageIcon, FileText, Settings, Menu, LogOut, ExternalLink, Boxes, TicketPercent,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { mediaUrl } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useEffect } from "react";

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/admin/commandes", label: "Commandes", icon: ShoppingCart, id: "commandes" },
  { to: "/admin/tarifs", label: "Produits & Tarifs", icon: Tag, id: "tarifs" },
  { to: "/admin/stock", label: "Stock", icon: Boxes, id: "stock" },
  { to: "/admin/livraison", label: "Livraison", icon: Truck, id: "livraison" },
  { to: "/admin/points-retrait", label: "Points de retrait", icon: PackageCheck, id: "points-retrait" },
  { to: "/admin/points-vente", label: "Points de vente", icon: MapPin, id: "points-vente" },
  { to: "/admin/entreprises", label: "Entreprises", icon: Building2, id: "entreprises" },
  { to: "/admin/promos", label: "Codes promo", icon: TicketPercent, id: "promos" },
  { to: "/admin/avis", label: "Avis", icon: Star, id: "avis" },
  { to: "/admin/blog", label: "Blog", icon: Newspaper, id: "blog" },
  { to: "/admin/medias", label: "Médias", icon: ImageIcon, id: "medias" },
  { to: "/admin/contenu", label: "Contenu du site", icon: FileText, id: "contenu" },
  { to: "/admin/parametres", label: "Paramètres", icon: Settings, id: "parametres" },
];

function NavItems({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-0.5" data-testid="admin-nav">
      {NAV.map(({ to, label, icon: Icon, end, id }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`admin-nav-${id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 text-sm font-semibold transition-colors ${
              isActive ? "bg-rouge text-white" : "text-cream/65 hover:text-cream hover:bg-cream/5"}`}>
          <Icon className="w-4 h-4 shrink-0" /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (user === null) {
    return (
      <div className="min-h-screen bg-onyx flex items-center justify-center" data-testid="admin-loading">
        <p className="text-cream/60 text-sm">Chargement…</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-sand flex" data-testid="admin-layout">
      <aside className="hidden lg:flex flex-col w-64 bg-onyx shrink-0 sticky top-0 h-screen">
        <div className="p-6 border-b border-cream/10">
          <img src={mediaUrl("/api/uploads/logo-blanc-transparent.png")} alt="Douzième Homme" className="h-14 w-auto mb-2" />
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-rouge-light font-bold mt-1">Back-office</p>
        </div>
        <div className="flex-1 overflow-y-auto py-4">
          <NavItems />
        </div>
        <div className="p-4 border-t border-cream/10 space-y-1">
          <Link to="/" data-testid="admin-view-site" className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-cream/65 hover:text-cream transition-colors">
            <ExternalLink className="w-4 h-4" /> Voir le site
          </Link>
          <button onClick={logout} data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-cream/65 hover:text-rouge-light transition-colors">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-40 bg-onyx text-cream flex items-center justify-between px-4 h-14">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button aria-label="Menu admin" data-testid="admin-mobile-menu" className="p-2 -ml-2">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-onyx border-cream/10 w-[80vw] sm:w-72 p-0 text-cream">
              <div className="p-6 border-b border-cream/10">
                <img src={mediaUrl("/api/uploads/logo-blanc-transparent.png")} alt="Douzième Homme" className="h-14 w-auto mb-2" />
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-rouge-light font-bold mt-1">Back-office</p>
              </div>
              <div className="py-4 overflow-y-auto max-h-[70vh]">
                <NavItems onNavigate={() => setOpen(false)} />
              </div>
              <div className="p-4 border-t border-cream/10 space-y-1">
                <Link to="/" className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-cream/65">
                  <ExternalLink className="w-4 h-4" /> Voir le site
                </Link>
                <button onClick={logout} data-testid="admin-logout-mobile"
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-cream/65">
                  <LogOut className="w-4 h-4" /> Déconnexion
                </button>
              </div>
            </SheetContent>
          </Sheet>
          <p className="font-display font-extrabold tracking-tight">DOUZIÈME HOMME</p>
          <span className="text-xs text-cream/50 truncate max-w-[40vw]">{user?.email}</span>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-10 max-w-6xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
